import axios, { AxiosResponse } from 'axios'
import {
  AtendimentoType,
  CategoriaNotificacaoType,
  ContratoType,
  CriterioAtendimentoType,
  FormularioSituacao,
  GeneroType,
  ModalidadeType,
  RacaType,
  SegmentoNotificacaoType,
  SituacaoDiagnosticoType,
  SituacaoFatura,
  SituacaoInterconsulta,
  SituacaoNota,
  StatusType,
  VinculoType,
} from '../enums'
import {
  Enum,
  EtniaList,
  NacionalidadeView,
  PageResponseMunicipioList,
  PageResponseNacionalidadeList,
  PageResponsePaisList,
  MotivoNaoRealizacaoEnum,
  VersionAPI,
} from './models'
import { cachedRequest } from '../requestCache'

const API_URL = process.env.REACT_APP_API_URL

export const getTiposContrato = (): Promise<Enum<ContratoType>[]> => {
  return cachedRequest('tipos/contrato', () =>
    axios
      .get(`${API_URL}/tipos/contrato`)
      .then((resp: AxiosResponse<Enum<ContratoType>[]>) => resp.data)
  )
}

/**
 * Tipos de Documentos Pessoais
 * @returns Enum
 */
export const getTiposDocumentoPessoal = (): Promise<Enum[]> => {
  return axios
    .get(`${API_URL}/tipos/documentoPessoal`)
    .then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Tipos de Atendimento
 * @returns Enum[]
 */
export const getTiposAtendimentos = (): Promise<Enum[]> => {
  return cachedRequest('tipos/atendimento', () =>
    axios.get(`${API_URL}/tipos/atendimento`).then((d: AxiosResponse<Enum[]>) => d.data)
  )
}

/**
 * Tipo de Atendimento
 * @param name AtendimentoType
 * @returns Enum
 */
export const getTiposAtendimentosByName = (name: AtendimentoType): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/atendimento/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Fatores de Risco
 * @returns Enum[]
 */
export const getFatoresDeRiscos = (): Promise<Enum[]> => {
  return axios.get(`${API_URL}/fatoresRisco`).then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Versão do Sistema
 * @returns VersionAPI
 */
export const getAPIVersion = (): Promise<VersionAPI> => {
  return axios.get(`${API_URL}/public/versao`).then((resp: AxiosResponse<VersionAPI>) => resp.data)
}

/**
 * Modalidades de Atendimento
 * @returns Enum[]
 */
export const getModalidadesAtendimento = (): Promise<Enum[]> => {
  return axios
    .get(`${API_URL}/tipos/atendimento/modalidade`)
    .then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Retorna modalidade específica
 * @param name ModalidadeType
 * @returns Enum
 */
export const getModalidadesAtendimentoByName = (name: ModalidadeType): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/atendimento/modalidade/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Tipos de Assinatura
 * @returns Enum[]
 */
export const getTiposAssinaturas = (): Promise<Enum[]> => {
  return axios.get(`${API_URL}/tipos/assinatura`).then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Tipos de Assinatura
 * @param name ModalidadeType
 * @returns Enum
 */
export const getTiposAssinaturasByName = (name: ModalidadeType): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/assinatura/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Tipos de Vinculo
 * @returns Enum[]
 */
export const getTiposOfVinculo = (): Promise<Enum[]> => {
  return axios.get(`${API_URL}/tipos/vinculo`).then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Tipos de Vinculo
 * @param name VinculoType
 * @returns Enum
 */
export const getTiposOfVinculoByName = (name: VinculoType): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/vinculo/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Situações de Formulário
 * @returns Enum[]
 */
export const getTiposOfFormularioSituacao = (): Promise<Enum[]> => {
  return axios
    .get(`${API_URL}/tipos/formulario/situacao`)
    .then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Situação de Formulário
 * @param name FormularioSituacao
 * @returns Enum
 */
export const getTiposOfFormularioSituacaoByName = (name: FormularioSituacao): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/formulario/situacao/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Gênero
 * @returns Enum[]
 */
export const getGeneros = (): Promise<Enum[]> => {
  return axios.get(`${API_URL}/generos`).then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Gênero
 * @param name GêneroType
 * @returns Enum
 */
export const getGenerosByName = (name: GeneroType): Promise<Enum> => {
  return axios.get(`${API_URL}/generos/${name}`).then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Raças
 * @returns Enum[]
 */
export const getRacas = (): Promise<Enum[]> => {
  return axios.get(`${API_URL}/racas`).then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Raças
 * @param name RacaType
 * @returns Enum
 */
export const getRacasByName = (name: RacaType): Promise<Enum> => {
  return axios.get(`${API_URL}/racas/${name}`).then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Busca todas as nacionalidade
 * @param query string
 * @returns PageResponseNacionalidadeList
 */
export const getNacionalidades = (query: string): Promise<PageResponseNacionalidadeList> => {
  return axios
    .get(`${API_URL}/nacionalidades?${query}`)
    .then((resp: AxiosResponse<PageResponseNacionalidadeList>) => resp.data)
}

/**
 * Municípios do catálogo IBGE de uma UF (rota autenticada da F1 do epic e-SUS FCI).
 *
 * A rota é paginada e aceita `nome` como filtro "contém" (sem acento, sem caixa) — quem monta a
 * query string é o chamador (`getOptionsMunicipiosPorUf`), no molde de `getNacionalidades`.
 *
 * `cachedRequest` mantido: o catálogo é estático e a mesma página costuma ser pedida mais de uma vez
 * numa sessão (abrir e fechar o cadastro do paciente), além do dedupe de requisição em voo. A chave
 * carrega a query string **inteira** de propósito: sem `page`/`size`/`sort`/`nome` nela, a rolagem
 * para a página 1 dentro da janela de TTL devolveria a página 0 do cache e o scroll infinito
 * travaria em silêncio.
 * @param uf Sigla da unidade federativa (ex.: `RS`)
 * @param query Query string já montada (`page`, `size`, `sort`, `nome`)
 * @returns PageResponseMunicipioList
 */
export const getMunicipiosPorUf = (
  uf: string,
  query: string
): Promise<PageResponseMunicipioList> => {
  return cachedRequest(`municipios/uf/${uf}?${query}`, () =>
    axios
      .get(`${API_URL}/municipios/uf/${uf}?${query}`)
      .then((resp: AxiosResponse<PageResponseMunicipioList>) => resp.data)
  )
}

/**
 * Catálogo de países do LEDI 8.4.2 (rota autenticada da F2b).
 *
 * Paginado e com filtro `nome`, pelos mesmos motivos de `getMunicipiosPorUf` — inclusive a chave de
 * cache carregando a query string inteira.
 * @param query Query string já montada (`page`, `size`, `sort`, `nome`)
 * @returns PageResponsePaisList
 */
export const getPaises = (query: string): Promise<PageResponsePaisList> => {
  return cachedRequest(`paises?${query}`, () =>
    axios
      .get(`${API_URL}/paises?${query}`)
      .then((resp: AxiosResponse<PageResponsePaisList>) => resp.data)
  )
}

/**
 * Catálogo de etnias indígenas do LEDI 8.4.2 (rota autenticada da F2b).
 * @returns EtniaList[]
 */
export const getEtnias = (): Promise<EtniaList[]> => {
  return cachedRequest('etnias', () =>
    axios.get(`${API_URL}/etnias`).then((resp: AxiosResponse<EtniaList[]>) => resp.data)
  )
}

/**
 * Busca uma nacionalidade
 * @param nacionalidadeId Identificador da nacionalidade
 * @returns NacionalidadeView
 */
export const getNacionalidadeById = (nacionalidadeId: number): Promise<NacionalidadeView> => {
  return axios
    .get(`${API_URL}/nacionalidades/${nacionalidadeId}`)
    .then((resp: AxiosResponse<NacionalidadeView>) => resp.data)
}

/**
 * Busca situações de Fatura
 * @returns Enum[]
 */
export const getSituacoesFatura = (): Promise<Enum[]> => {
  return axios
    .get(`${API_URL}/tipos/fatura/situacao`)
    .then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Busca uma situação de Fatura
 * @param name SituacaoFatura
 * @returns Enum
 */
export const getSituacoesFaturaByName = (name: SituacaoFatura): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/fatura/situacao/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Busca situações de Nota
 * @returns Enum[]
 */
export const getSituacoesNota = (): Promise<Enum[]> => {
  return axios
    .get(`${API_URL}/tipos/nota/situacao`)
    .then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Busca uma situação de Nota
 * @param name SituacaoNota
 * @returns Enum
 */
export const getSituacoesNotaByName = (name: SituacaoNota): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/nota/situacao/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Status de Validação de Nota
 * @returns Enum[]
 */
export const getTiposStatusNota = (): Promise<Enum[]> => {
  return axios
    .get(`${API_URL}/tipos/nota/validacao/status`)
    .then((resp: AxiosResponse<Enum[]>) => resp.data)
}

/**
 * Status de validação de nota
 * @param name StatusType
 * @returns Enum
 */
export const getTiposStatusNotaByName = (name: StatusType): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/nota/validacao/status/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Motivos de não realização de atendimento (cancelamento e falta).
 *
 * Usa a rota **pública** de propósito: ela serve os dois fluxos (o interno, já autenticado, e a
 * tela de cancelamento pelo paciente, que monta o select antes de autenticar por token de
 * atendimento). É dado de referência global, sem paciente e sem escopo de tenant.
 *
 * @returns MotivoNaoRealizacaoEnum[]
 */
export const getMotivosNaoRealizacao = (): Promise<MotivoNaoRealizacaoEnum[]> => {
  return cachedRequest('public/paciente/motivos-nao-realizacao', () =>
    axios
      .get(`${API_URL}/public/paciente/motivos-nao-realizacao`)
      .then((resp: AxiosResponse<MotivoNaoRealizacaoEnum[]>) => resp.data)
  )
}

/**
 * Situações da interconsulta (visão do solicitante)
 * @returns Enum[]
 */
export const getTiposSituacoesInterconsulta = (): Promise<Enum<SituacaoInterconsulta>[]> => {
  return cachedRequest('tipos/interconsulta/situacao', () =>
    axios
      .get(`${API_URL}/tipos/interconsulta/situacao`)
      .then((resp: AxiosResponse<Enum<SituacaoInterconsulta>[]>) => resp.data)
  )
}

/**
 * Situações de Diagnóstico
 * @returns Enum[]
 */
export const getTiposSituacoesDiagnostico = (): Promise<Enum[]> => {
  return cachedRequest('tipos/atendimento/situacao-diagnostico', () =>
    axios
      .get(`${API_URL}/tipos/atendimento/situacao-diagnostico`)
      .then((resp: AxiosResponse<Enum[]>) => resp.data)
  )
}

/**
 * Situações de Diagnóstico
 * @param name SituacaoDiagnosticoType
 * @returns Enum
 */
export const getTiposSituacoesDiagnosticoByName = (
  name: SituacaoDiagnosticoType
): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/atendimento/situacao-diagnostico/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Busca todas as categorias de notificação
 * @returns Enum[]
 */
export const getTiposCategoriasNotificacao = (): Promise<Enum[]> => {
  return cachedRequest('tipos/notificacao/categoria', () =>
    axios
      .get(`${API_URL}/tipos/notificacao/categoria`)
      .then((resp: AxiosResponse<Enum[]>) => resp.data)
  )
}

/**
 * Causas de interrupção de teleconsulta
 *
 * Lista fechada servida pelo backend; o front não mantém enum paralelo.
 * @returns Enum[]
 */
export const getCausasInterrupcao = (): Promise<Enum[]> => {
  return cachedRequest('tipos/atendimento/interrupcao/causa', () =>
    axios
      .get(`${API_URL}/tipos/atendimento/interrupcao/causa`)
      .then((resp: AxiosResponse<Enum[]>) => resp.data)
  )
}

/**
 * Busca uma categoria de notificação
 * @param name CategoriaNotificacaoType
 * @returns Enum
 */
export const getTiposCategoriasNotificacaoByName = (
  name: CategoriaNotificacaoType
): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/notificacao/categoria/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Busca todos os segmentos de envio de notificação
 * @returns Enum[]
 */
export const getTiposSegmentosNotificacao = (): Promise<Enum[]> => {
  return cachedRequest('tipos/notificacao/segmento', () =>
    axios
      .get(`${API_URL}/tipos/notificacao/segmento`)
      .then((resp: AxiosResponse<Enum[]>) => resp.data)
  )
}

/**
 * Busca um segmento de envio de notificação
 * @param name SegmentoNotificacaoType
 * @returns Enum
 */
export const getTiposSegmentosNotificacaoByName = (
  name: SegmentoNotificacaoType
): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/notificacao/segmento/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Busca todos os critérios de atendimento em aberto
 * @returns Enum[]
 */
export const getTiposCriterioAtendimento = (): Promise<Enum[]> => {
  return cachedRequest('tipos/atendimento/criterio', () =>
    axios
      .get(`${API_URL}/tipos/atendimento/criterio`)
      .then((resp: AxiosResponse<Enum[]>) => resp.data)
  )
}

/**
 * Busca um critério de atendimento
 * @param name CriterioAtendimentoType
 * @returns Enum
 */
export const getTiposCriterioAtendimentoByName = (name: CriterioAtendimentoType): Promise<Enum> => {
  return axios
    .get(`${API_URL}/tipos/atendimento/criterio/${name}`)
    .then((resp: AxiosResponse<Enum>) => resp.data)
}

/**
 * Opções de quem perdeu a conexão numa interrupção de teleconsulta
 * @returns Enum[]
 */
export const getQuemCaiuInterrupcao = (): Promise<Enum[]> => {
  return cachedRequest('tipos/atendimento/interrupcao/quem-caiu', () =>
    axios
      .get(`${API_URL}/tipos/atendimento/interrupcao/quem-caiu`)
      .then((resp: AxiosResponse<Enum[]>) => resp.data)
  )
}
